import type { AuditSession } from '@cca/ui';
import { reportTelemetry } from '@cca/ui';
import type {
  PersonnelPerson,
  PersonnelData,
  MergeDirectives,
  MeResponse,
  HealthResponse,
  ParishListResponse,
  AccessRequestCreateResponse,
  AccessRequestRow,
  AccessCancelResponse,
  AccessApproveResponse,
  AccessHoldersResponse,
  AccessRevokeResponse,
  PersonnelDryRunResponse,
  PersonnelSubmitResult,
  EligibleEditorsResponse,
  NotifyUpdateResult,
} from '@cca/parishes-shared';

export type {
  PersonnelPerson,
  SeniorMinisterPerson,
  OtherClergryPerson,
  PersonnelData,
  CandidateMatch,
  SameParishMatch,
  OtherParishMatch,
  PersonChangeSet,
  PersonnelDiff,
  MergeDirectives,
  ParishSummary,
  PendingAccessRequest,
  MeResponse,
  HealthResponse,
  ParishListItem,
  PriorRequestSummary,
  AccessRequestRow,
  AccessHolder,
  PersonnelSubmitResult,
  EligibleEditor,
  EligibleEditorsResponse,
  NotifyUpdateResult,
} from '@cca/parishes-shared';

/** The explicit "someone new" answer to a same-parish match (cca-0x72d). */
export { CREATE_NEW } from '@cca/parishes-shared';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new ApiError(`GET ${path} failed: HTTP ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

async function del<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(
      `DELETE ${path} failed: HTTP ${res.status}${json.error ? ` (${String(json.error)})` : ''}`,
      res.status,
    );
  }
  return json as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // CSRF defense-in-depth — Worker rejects mutating requests without it.
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(
      `POST ${path} failed: HTTP ${res.status}${json.error ? ` (${String(json.error)})` : ''}`,
      res.status,
      typeof json.message === 'string' ? json.message : undefined,
    );
  }
  return json as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    /**
     * The worker's own plain sentence for the user, when it sent one (e.g. the
     * approve route's "ask the Registrar" refusals, cca-0x72d). Shown instead
     * of the technical `message` where a page wants to.
     */
    public serverMessage?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    // Telemetry (Phase 4): surface server errors even when the app catches them.
    // 4xx (auth/validation) are expected, so only 5xx is reported.
    if (status >= 500) reportTelemetry('api_error', message);
  }
}

/**
 * The form's NameField uses `last_name = ''` mid-typing to preserve a trailing
 * space in the input display. Coerce back to `null` before sending so the
 * server doesn't see spurious "Last_Name changed from null to ''" edits.
 */
function cleanPerson<P extends PersonnelPerson>(p: P): P {
  return {
    ...p,
    first_name: p.first_name === '' ? null : p.first_name,
    last_name: p.last_name === '' ? null : p.last_name,
  };
}

function normalizePersonnel(data: PersonnelData): PersonnelData {
  return {
    ...data,
    senior_minister: data.senior_minister ? cleanPerson(data.senior_minister) : null,
    other_clergy: data.other_clergy.map(cleanPerson),
    wardens: {
      vicars_warden: data.wardens.vicars_warden ? cleanPerson(data.wardens.vicars_warden) : null,
      bishops_warden: data.wardens.bishops_warden ? cleanPerson(data.wardens.bishops_warden) : null,
      peoples_warden: data.wardens.peoples_warden ? cleanPerson(data.wardens.peoples_warden) : null,
    },
    parish_council: data.parish_council.map(cleanPerson),
    synod_reps: data.synod_reps.map(cleanPerson),
    vpso: data.vpso ? cleanPerson(data.vpso) : null,
    safety_lead_officer: data.safety_lead_officer ? cleanPerson(data.safety_lead_officer) : null,
    treasurer: data.treasurer ? cleanPerson(data.treasurer) : null,
    administrator: data.administrator ? cleanPerson(data.administrator) : null,
    nominators: data.nominators.map(cleanPerson),
  };
}

export const api = {
  me: () => get<MeResponse>('/api/me'),
  /** Registrar-only login + activity audit trail. */
  loginHistory: () => get<{ sessions: AuditSession[] }>('/api/auth/login-history'),
  health: () => get<HealthResponse>('/api/health'),
  personnel: (parishId: string) => get<PersonnelData>(`/api/parishes/${parishId}/personnel`),
  personnelDryRun: (parishId: string, body: PersonnelData) =>
    post<PersonnelDryRunResponse>(
      `/api/parishes/${parishId}/personnel?dryRun=1`,
      normalizePersonnel(body),
    ),
  personnelSubmit: (parishId: string, body: PersonnelData, merges: MergeDirectives = {}) =>
    post<PersonnelSubmitResult>(`/api/parishes/${parishId}/personnel`, {
      ...normalizePersonnel(body),
      merges,
    }),
  // Access request flow
  listAllParishes: () => get<ParishListResponse>('/api/access/parishes'),
  requestAccess: (parishId: string, reason: string) =>
    post<AccessRequestCreateResponse>('/api/access/request', { parishId, reason }),
  getAccessRequest: (token: string) => get<AccessRequestRow>(`/api/access/request/${token}`),
  cancelAccessRequest: (token: string) => del<AccessCancelResponse>(`/api/access/request/${token}`),
  /** No body: linking to an existing Contact is the Registrar's job (cca-0x72d). */
  approveAccessRequest: (token: string) =>
    post<AccessApproveResponse>(`/api/access/approve/${token}`, {}),
  // Per-parish access management (Senior Minister only)
  accessHolders: (parishId: string) =>
    get<AccessHoldersResponse>(`/api/parishes/${parishId}/access/holders`),
  revokeAccess: (parishId: string, contactId: string, reason?: string) =>
    post<AccessRevokeResponse>(`/api/parishes/${parishId}/access/revoke`, {
      contactId,
      reason,
    }),
  // Registrar update-request tool (Admin drawer)
  eligibleEditors: (parishId: string) =>
    get<EligibleEditorsResponse>(`/api/admin/parishes/${parishId}/eligible-editors`),
  notifyUpdate: (
    parishId: string,
    recipients: { email: string; name?: string }[],
    message?: string,
  ) => post<NotifyUpdateResult>('/api/admin/notify-update', { parishId, recipients, message }),
};
